import { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, UseFormSetValue } from "react-hook-form";
import { Close, ExpandMore } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  MenuItem,
  Select,
  Stack,
  useTheme,
  Typography as T,
} from "@mui/material";
import FileSelect from "datasets/pages/tables/components/FileSelect";
import {
  StyledLoadingButton,
  StyledOutlinedInput,
} from "datasets/pages/tables/components/ImportDetails";
import useFileMetadata from "datasets/pages/tables/hooks/useFileMetadata";
import {
  Ingest,
  IngestColumn,
  IngestMetadata,
} from "datasets/pages/tables/types/interfaces/ingest";
import { resolveRouterPath } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import yn from "yn";

const { VITE_DATASET_INGESTION_JOBS_ENABLED } = import.meta.env;

/**
 * Validates that metadata.json columns match exactly with form columns.
 * Returns error message if mismatch, null if valid.
 */
const validateColumnMatch = (
  formColumns: IngestColumn[],
  metadataColumns: IngestColumn[]
): string | null => {
  if (formColumns.length !== metadataColumns.length) {
    return `Column count mismatch: form has ${formColumns.length} columns, metadata file has ${metadataColumns.length} columns.`;
  }

  const formNames = new Set(formColumns.map((c) => c.name.toUpperCase()));
  const metaNames = new Set(metadataColumns.map((c) => c.name.toUpperCase()));

  const missingInMeta = [...formNames].filter((n) => !metaNames.has(n));
  const extraInMeta = [...metaNames].filter((n) => !formNames.has(n));

  if (missingInMeta.length > 0 || extraInMeta.length > 0) {
    const errors: string[] = [];
    if (missingInMeta.length > 0) {
      errors.push(`Columns missing in metadata file: ${missingInMeta.join(", ")}`);
    }
    if (extraInMeta.length > 0) {
      errors.push(`Extra columns in metadata file: ${extraInMeta.join(", ")}`);
    }
    return errors.join(" ");
  }

  return null;
};

type MetadataModalForm = {
  metadata_platform_storage_key: string;
  metadata_config: IngestMetadata;
};

const DEFAULT_FILE_FORMAT = {
  type: "csv",
  delimiter: ",",
  skip_header: 1,
  encoding: "UTF8",
  null_if: ["\\N", "NULL", ""],
};

const DELIMITER_OPTIONS = [
  { value: ",", label: "Comma" },
  { value: "\t", label: "Tab" },
];

const ENCODING_OPTIONS = [{ value: "UTF8", label: "UTF-8" }];

const SourceFileConfigurationModal = ({
  open,
  onClose,
  metadataFileName,
  metadataConfig,
  setIngestFormValue,
}: {
  open: boolean;
  onClose: () => void;
  metadataFileName: string;
  metadataConfig: IngestMetadata | undefined;
  setIngestFormValue: UseFormSetValue<Ingest>;
}) => {
  const theme = useTheme();
  const [nullIfInput, setNullIfInput] = useState("");
  const [popperAnchorEl, setPopperAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMetadataFile, setSelectedMetadataFile] = useState<null | FileInfo>(null);
  const [metadataIngestError, setMetadataIngestError] = useState<Error | undefined>(undefined);
  const [columnMatchError, setColumnMatchError] = useState<string | null>(null);
  const [selectedMetadataFileKey, setSelectedMetadataFileKey] = useState<string | undefined>(
    undefined
  );

  const {
    data: selectedFileMetadata,
    error: selectedFileError,
    isLoading: isLoadingSelectedFile,
  } = useFileMetadata(selectedMetadataFileKey);

  const metadataForm = useForm<MetadataModalForm>({
    defaultValues: {
      metadata_platform_storage_key: metadataFileName,
      metadata_config: metadataConfig ?? {
        file_format: DEFAULT_FILE_FORMAT,
      },
    },
  });

  const { setValue, resetField, watch, formState, reset } = metadataForm;

  const nullIfValues = watch("metadata_config.file_format.null_if");

  // Reset form when modal opens to sync with parent form props
  useEffect(() => {
    if (open) {
      reset({
        metadata_platform_storage_key: metadataFileName,
        metadata_config: {
          ...metadataConfig,
          file_format: metadataConfig?.file_format ?? DEFAULT_FILE_FORMAT,
        },
      });
      setSelectedMetadataFileKey(undefined);
      setMetadataIngestError(undefined);
      setColumnMatchError(null);
    }
  }, [open, metadataFileName, metadataConfig, reset]);

  // Handle the metadata file selection and data fetching with column validation
  useEffect(() => {
    if (!selectedMetadataFileKey) {
      return;
    }
    setValue("metadata_platform_storage_key", selectedMetadataFileKey, { shouldDirty: true });
    if (selectedFileMetadata) {
      const currentColumns = metadataConfig?.columns || [];
      const isFileValidationEnabled = yn(VITE_DATASET_INGESTION_JOBS_ENABLED);

      if (!isFileValidationEnabled) {
        // metadata.json replaces all form values
        setColumnMatchError(null);
        setValue("metadata_config", selectedFileMetadata, { shouldDirty: true });
      } else if (currentColumns.length === 0) {
        // No columns yet, populate from metadata.json
        setColumnMatchError(null);
        setValue("metadata_config", selectedFileMetadata, { shouldDirty: true });
      } else {
        // Validate columns match DataProfile before populating
        const validationError = validateColumnMatch(currentColumns, selectedFileMetadata.columns);

        if (validationError) {
          // Show as warning, still populate but warn user about mismatch
          setColumnMatchError(validationError);
        } else {
          setColumnMatchError(null);
        }
        // Always populate the form - let onSave handle the merge logic
        setValue("metadata_config", selectedFileMetadata, { shouldDirty: true });
      }

      setMetadataIngestError(undefined);
      setPopperAnchorEl(null);
    } else if (selectedFileError) {
      setMetadataIngestError(selectedFileError);
      setColumnMatchError(null);
      setPopperAnchorEl(null);
    }
  }, [
    selectedFileMetadata,
    selectedFileError,
    selectedMetadataFileKey,
    setValue,
    resetField,
    metadataForm,
    metadataConfig?.columns,
  ]);

  const handleSelectMetadataFile = () => {
    if (selectedMetadataFile) {
      setSelectedMetadataFileKey(selectedMetadataFile.key);
      // The useEffect above will handle the data fetching and form updates
    }
  };

  const handlePopperOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setPopperAnchorEl(event.currentTarget);
  };

  const handleAddNullIf = () => {
    if (nullIfInput.trim() && !nullIfValues?.includes(nullIfInput.trim())) {
      const newValues = [...(nullIfValues || []), nullIfInput.trim()];
      setValue("metadata_config.file_format.null_if", newValues, { shouldDirty: true });
      setNullIfInput("");
    }
  };

  const handleRemoveNullIf = (valueToRemove: string) => {
    const newValues = nullIfValues?.filter((value) => value !== valueToRemove);
    setValue("metadata_config.file_format.null_if", newValues, { shouldDirty: true });
  };

  const handleNullIfKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddNullIf();
    }
  };

  const onSave = (data: MetadataModalForm) => {
    const newFileIngested = data.metadata_platform_storage_key !== metadataFileName;

    if (newFileIngested) {
      // User explicitly selected a metadata.json file - this is INTENTIONAL
      // Replace ALL form values with the response data
      // For flag ON: validation already happened in useEffect (warning shown if mismatch)
      // For flag OFF: no validation needed
      setIngestFormValue("metadata_config", data.metadata_config);
      setIngestFormValue("metadata_platform_storage_key", data.metadata_platform_storage_key);
    } else {
      // Same file, just updating file format settings
      setIngestFormValue("metadata_config.file_format", data.metadata_config.file_format);
    }
    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const MetadataSelectErrorText = () => {
    // Show column match issue as warning (new flow) or error (old flow)
    if (columnMatchError) {
      const isFileValidationEnabled = yn(VITE_DATASET_INGESTION_JOBS_ENABLED);
      return (
        <Alert severity={isFileValidationEnabled ? "warning" : "error"} sx={{ mt: 1 }}>
          {columnMatchError}
          {isFileValidationEnabled && " Only matching columns will be enhanced."}
        </Alert>
      );
    }
    if (metadataIngestError && Array.isArray(metadataIngestError.message)) {
      return (
        <List disablePadding>
          {metadataIngestError.message.map((err: string, i: number) => (
            <ListItem key={i} disableGutters disablePadding sx={{ gap: 0.5 }}>
              <ListItemIcon sx={{ minWidth: "unset" }}>
                <Close color="error" sx={{ width: 16 }} />
              </ListItemIcon>
              <T variant="caption" color="attention.main">
                {err}
              </T>
            </ListItem>
          ))}
        </List>
      );
    }
    if (metadataIngestError?.message) {
      return (
        <T variant="caption" color={"attention.main"}>
          {metadataIngestError.message}
        </T>
      );
    }
    return null;
  };

  return (
    <>
      <FormProvider {...metadataForm}>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Source File Configuration</DialogTitle>
          <DialogContent>
            <Stack gap={2}>
              <Stack gap={0.5} position="relative">
                <T variant="body2" fontWeight={500}>
                  Metadata File
                </T>
                <Controller
                  control={metadataForm.control}
                  name="metadata_platform_storage_key"
                  render={({ field }) => (
                    <StyledLoadingButton
                      size="small"
                      endIcon={<ExpandMore />}
                      loading={isLoadingSelectedFile}
                      loadingPosition="end"
                      onClick={handlePopperOpen}
                      sx={{
                        justifyContent: "space-between",
                        flex: 1,
                        minWidth: 0,
                      }}
                      variant="outlined"
                    >
                      <T
                        fontSize="inherit"
                        sx={{
                          opacity: field.value ? 1 : 0.6,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          minWidth: 0,
                          textAlign: "left",
                        }}
                      >
                        {field.value ? resolveRouterPath(field.value) : "Select metadata file..."}
                      </T>
                    </StyledLoadingButton>
                  )}
                />
                <FileSelect
                  popperAnchorEl={popperAnchorEl}
                  handlePopperClose={() => setPopperAnchorEl(null)}
                  selectedFile={selectedMetadataFile}
                  setSelectedFile={setSelectedMetadataFile}
                  onSelectFile={handleSelectMetadataFile}
                  loading={isLoadingSelectedFile}
                  maxHeight={310}
                />
                {<MetadataSelectErrorText />}
              </Stack>
              <Stack gap={0.5}>
                <T variant="body2" fontWeight={500}>
                  Delimiter
                </T>
                <Controller
                  control={metadataForm.control}
                  name="metadata_config.file_format.delimiter"
                  defaultValue=","
                  render={({ field }) => {
                    const unknownDelimiterOption =
                      field.value !== "" && !DELIMITER_OPTIONS.find((o) => o.value === field.value)
                        ? {
                            value: field.value,
                            label: field.value === " " ? "Space" : field.value,
                          }
                        : null;
                    return (
                      <Select {...field} size="small" fullWidth>
                        {DELIMITER_OPTIONS.map((option) => (
                          <MenuItem key={`${option.value}`} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                        {unknownDelimiterOption && (
                          <MenuItem
                            key={`${unknownDelimiterOption.value}`}
                            value={unknownDelimiterOption.value}
                          >
                            {unknownDelimiterOption.label}
                          </MenuItem>
                        )}
                      </Select>
                    );
                  }}
                />
              </Stack>
              <Stack gap={0.5}>
                <T variant="body2" fontWeight={500}>
                  Skip Header
                </T>
                <Controller
                  control={metadataForm.control}
                  name="metadata_config.file_format.skip_header"
                  defaultValue={1}
                  render={({ field }) => (
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.grey[300]}`,
                        borderRadius: 1,
                        p: 1.5,
                        backgroundColor: field.value === 1 ? "primary.light" : "grey.50",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: field.value === 1 ? "primary.light" : "grey.100",
                        },
                      }}
                      onClick={() => field.onChange(field.value === 1 ? 0 : 1)}
                    >
                      <Box display="flex" gap={1.5} alignItems="center">
                        <Checkbox
                          checked={field.value === 1}
                          size="small"
                          onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                          sx={{
                            color: field.value === 1 ? "primary.main" : "grey.500",
                            "&.Mui-checked": {
                              color: "primary.main",
                            },
                          }}
                        />
                        <Stack gap={0.25}>
                          <T variant="body2" fontWeight={500}>
                            Skip header row on import
                          </T>
                          <T variant="caption" color="text.secondary">
                            Exclude the first row when importing data
                          </T>
                        </Stack>
                      </Box>
                    </Box>
                  )}
                />
              </Stack>
              <Stack gap={0.5}>
                <T variant="body2" fontWeight={500}>
                  Null If
                </T>
                <Controller
                  control={metadataForm.control}
                  name="metadata_config.file_format.null_if"
                  render={({ field }) => (
                    <Stack gap={1}>
                      <StyledOutlinedInput
                        fullWidth
                        placeholder="Add null value..."
                        size="small"
                        value={nullIfInput}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setNullIfInput(e.target.value)
                        }
                        onKeyPress={handleNullIfKeyPress}
                        onBlur={handleAddNullIf}
                      />
                      {field.value && field.value.length > 0 && (
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {field.value.map((value, index) => (
                            <Chip
                              key={index}
                              label={value === "" ? `""` : value}
                              size="small"
                              onDelete={() => handleRemoveNullIf(value)}
                            />
                          ))}
                        </Box>
                      )}
                    </Stack>
                  )}
                />
              </Stack>
              <Stack gap={0.5}>
                <T variant="body2" fontWeight={500}>
                  Encoding
                </T>
                <Controller
                  control={metadataForm.control}
                  name="metadata_config.file_format.encoding"
                  defaultValue="UTF8"
                  render={({ field }) => {
                    const unknownEncodingOption =
                      field.value !== "" && !ENCODING_OPTIONS.find((o) => o.value === field.value)
                        ? { value: field.value, label: field.value }
                        : null;

                    return (
                      <Select {...field} size="small" fullWidth>
                        {ENCODING_OPTIONS.map((option) => (
                          <MenuItem key={`${option.value}`} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                        {unknownEncodingOption && (
                          <MenuItem
                            key={`${unknownEncodingOption.value}`}
                            value={unknownEncodingOption.value}
                          >
                            {unknownEncodingOption.label}
                          </MenuItem>
                        )}
                      </Select>
                    );
                  }}
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={metadataForm.handleSubmit(onSave)}
              disabled={
                !formState.isDirty ||
                // Flag OFF: column mismatch is blocking error
                // Flag ON: column mismatch is just a warning, allow save
                (!!columnMatchError && !yn(VITE_DATASET_INGESTION_JOBS_ENABLED))
              }
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </FormProvider>
    </>
  );
};
export default SourceFileConfigurationModal;
